import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { WorkspaceProvider } from "./contexts/Workspacecontext";
import ProtectedRoute from "./components/ProtectedRoute";
import ModuleGate from "./components/ModuleGate";

// --- Core ---
import SignIn from "./Login";
import Error500 from "./Pages/Error505";
import ErrorBoundary from "./ErrorBoundary";

// Workspace selector — shown right after login
import WorkspaceSelect from "./Pages/WorkspaceSelect/WorkspaceSelect";

// Public job apply — no auth, no sidebar
import JobApplyPage from "./Pages/JobApply/JobApplyPage";

// Public interview slot booking — no auth, no sidebar
import InterviewSlotBooking from "./Pages/Interviews/Interviewslotbooking";
import ProtectedAppRoute from "./components/Layout/ProtectedAppRoute";
import { protectedRoutes } from "./config/protectedRoutes";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./App.scss";

// ─── Workspace select — requires auth, no sidebar ────────────────────────────
const WS = () => (
  <ProtectedRoute>
    <WorkspaceSelect />
  </ProtectedRoute>
);

// ─── Public route prefixes ────────────────────────────────────────────────────
const PUBLIC_PATHS = [
  "/interview/select-slot/",
  "/consultancy/job-drive/",
  "/consultancy/jobs/",
  "/job-drive/",
  "/jobs/",
];

// ─── Inner app — must be inside <Router> so useLocation works ────────────────
const AppRoutes = () => {
  const { pathname } = useLocation();
  const isPublicRoute = PUBLIC_PATHS.some((p) => pathname.startsWith(p));

  if (isPublicRoute) {
    return (
      <Routes>
        <Route
          path="/interview/select-slot/:tenantB64/:interviewHistoryId"
          element={<InterviewSlotBooking />}
        />

        {/* ── COMPANY routes ── */}
        <Route path="/jobs/:tenantB64/:jobPostId" element={<JobApplyPage />} />
        <Route
          path="/jobs/:tenantB64/:jobPostId/drive/:jobDriveOrWalkinId"
          element={<JobApplyPage />}
        />
        <Route
          path="/job-drive/:tenantB64/:driveId"
          element={<JobApplyPage />}
        />

        {/* ── CONSULTANCY routes ── */}
        <Route
          path="/consultancy/jobs/:tenantB64/:jobPostId"
          element={<JobApplyPage />}
        />
        <Route
          path="/consultancy/jobs/:tenantB64/:jobPostId/drive/:jobDriveOrWalkinId"
          element={<JobApplyPage />}
        />
        <Route
          path="/consultancy/job-drive/:tenantB64/:driveId"
          element={<JobApplyPage />}
        />
      </Routes>
    );
  }

  return (
    <AuthProvider>
      <WorkspaceProvider>
        <Routes>
          <Route path="/" element={<SignIn />} />
          <Route path="/select-workspace" element={<WS />} />
          {protectedRoutes.map(
            ({ path, component: Component, redirectTo, moduleCodes }) => (
              <Route
                key={path}
                path={path}
                element={
                  redirectTo ? (
                    <Navigate to={redirectTo} replace />
                  ) : (
                    <ProtectedAppRoute
                      component={(props) => (
                        <ModuleGate codes={moduleCodes}>
                          <Component {...props} />
                        </ModuleGate>
                      )}
                    />
                  )
                }
              />
            ),
          )}
          <Route path="/error-500" element={<Error500 />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </WorkspaceProvider>
    </AuthProvider>
  );
};

// ─── App ──────────────────────────────────────────────────────────────────────
function App() {
  return (
    <>
      <Router>
        <ErrorBoundary fallback={<Error500 />}>
          <AppRoutes />
        </ErrorBoundary>
      </Router>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  );
}

export default App;
